import { useEffect, useState } from "react";
import { ScanLine, X, UserPlus, Search, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import QrScanner from "../../components/shared/QrScanner";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KIND_STYLE = { member: "bg-emerald-100 text-emerald-700", guest: "bg-sky-100 text-sky-700", unregistered: "bg-slate-100 text-slate-600", new: "bg-amber-100 text-amber-700" };
const KIND_LABEL = { member: "Member", guest: "Guest", unregistered: "Earlier buyer", new: "New email" };

/** Walk-in buyer: live search (name / email / wallet ID / QR scan) → tag an existing person, or use a new email, or skip. */
export function WalkinBuyerPicker({ eventId, value, onChange, onQueryChange }) {
  const { authAxios } = useAuth();
  const [q, setQRaw] = useState("");
  const setQ = (v) => { setQRaw(v); onQueryChange?.(v); };
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(false);

  useEffect(() => {
    if (value || q.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      authAxios.get(`/ticketing/accountant/events/${eventId}/walkin/people`, { params: { q: q.trim() } })
        .then(r => { if (!cancelled) setResults(r.data); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, value, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[#2d8a4e]/40 bg-green-50/50" data-testid="walkin-buyer-selected">
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${KIND_STYLE[value.kind]}`}>{KIND_LABEL[value.kind]}</span>
        <div className="flex-1 min-w-0 text-sm">
          <p className="font-semibold text-[#1e3a5f] truncate">{value.name || value.email}</p>
          <p className="text-[11px] text-slate-500 truncate">{[value.id, value.name ? value.email : ""].filter(Boolean).join(" · ")}</p>
        </div>
        <button onClick={() => { onChange(null); setQ(""); }} className="p-1 text-slate-400 hover:text-red-500" aria-label="Clear buyer" data-testid="walkin-buyer-clear"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  const typed = q.trim();
  const isEmail = EMAIL_RE.test(typed);
  const exists = results.some(r => r.email && r.email.toLowerCase() === typed.toLowerCase());
  return (
    <div className="relative" data-testid="walkin-buyer-picker">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buyer: name, email or wallet ID (optional)"
            className="w-full pl-9 pr-8 py-2 rounded-xl border text-sm" data-testid="walkin-buyer-search" />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        <button onClick={() => setScan(true)} className="px-3 rounded-xl border text-[#1e3a5f] hover:bg-slate-50" aria-label="Scan wallet QR" data-testid="walkin-buyer-scan"><ScanLine className="w-4 h-4" /></button>
      </div>
      {typed.length >= 2 && (
        <div className="mt-1 rounded-xl border bg-white shadow-sm divide-y max-h-60 overflow-y-auto" data-testid="walkin-buyer-results">
          {results.map((r, i) => (
            <button key={`${r.kind}-${r.id || r.email}`} onClick={() => onChange(r)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50" data-testid={`walkin-buyer-result-${i}`}>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${KIND_STYLE[r.kind]}`}>{KIND_LABEL[r.kind]}</span>
              <span className="flex-1 min-w-0 text-sm">
                <span className="block font-medium text-[#1e3a5f] truncate">{r.name || r.email}</span>
                <span className="block text-[11px] text-slate-500 truncate">{[r.id, r.name ? r.email : ""].filter(Boolean).join(" · ")}</span>
              </span>
            </button>
          ))}
          {!loading && isEmail && !exists && (
            <button onClick={() => onChange({ kind: "new", id: null, name: "", email: typed.toLowerCase() })} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50" data-testid="walkin-buyer-use-new">
              <UserPlus className="w-4 h-4" /> Use new email <strong className="truncate">{typed}</strong>
            </button>
          )}
          {!loading && !results.length && !isEmail && (
            <p className="px-3 py-2 text-xs text-slate-400" data-testid="walkin-buyer-none">No one found. Type a full email to add a new one, or leave empty to skip.</p>
          )}
        </div>
      )}
      {scan && <QrScanner onScan={(t) => { setScan(false); setQ(String(t || "").trim().toUpperCase()); }} onClose={() => setScan(false)} />}
    </div>
  );
}
